import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Landing.css';

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const CONTACT_EMAIL = 'k9200391@naver.com';
const KAKAO_CHANNEL_URL = 'http://pf.kakao.com/_eFiFX';
const NAVER_CAFE_URL = 'https://cafe.naver.com/plango5';
const CHARGING_PLANNER_URL = 'https://charging.plango.today';
const MAILTO_URL = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('[PlanGo.today 문의] ')}`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
};

const Landing = () => {
  const { token } = useAuth();
  const [contactOpen, setContactOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const openContact = (e) => {
    e.preventDefault();
    setCopied(false);
    setContactOpen(true);
  };

  useEffect(() => {
    if (!contactOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setContactOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [contactOpen]);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'PlanGo.today — 오늘의 관심이 내일의 가능성이 됩니다';
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const scrollTo = useCallback((id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, []);

  return (
    <div className="plango-landing">
      <header className="site-header">
        <div className="wrap nav">
          <a className="brand" href="#home" onClick={scrollTo('home')} aria-label="PlanGo.today 홈">
            <span className="brand-mark" aria-hidden="true">
              <svg {...svgProps} strokeWidth="1.9">
                <circle cx="6" cy="19" r="2.5" />
                <path d="M8.5 19h8.5a3.5 3.5 0 0 0 0-7H7a3.5 3.5 0 0 1 0-7h8.5" />
                <circle cx="18" cy="5" r="2.5" />
              </svg>
            </span>
            <span>PlanGo.today</span>
          </a>
          <nav className="nav-links" aria-label="주 메뉴">
            <a href="#about" onClick={scrollTo('about')}>소개</a>
            <a href="#interests" onClick={scrollTo('interests')}>관심사</a>
            <a href="#projects" onClick={scrollTo('projects')}>프로젝트</a>
            <a className="nav-contact" href="#contact" onClick={scrollTo('contact')}>연락하기</a>
            {token ? (
              <RouterLink className="nav-login" to="/dashboard">대시보드</RouterLink>
            ) : (
              <RouterLink className="nav-login" to="/login">PlanGo Login</RouterLink>
            )}
          </nav>
        </div>
      </header>

      <main id="home">
        <section className="hero">
          <div className="wrap hero-grid">
            <div>
              <div className="eyebrow">MY SPACE · MY JOURNEY</div>
              <h1>
                오늘의 관심이
                <br />
                <span>내일의 가능성</span>이 됩니다.
              </h1>
              <p className="hero-copy">
                새로운 것을 배우고, 직접 만들어 보고, 일상 속에서 발견한 것들을 기록합니다. 이곳은 관심사와
                경험, 그리고 작은 아이디어를 하나씩 모아 가는 개인 공간입니다.
              </p>
              <div className="hero-actions">
                <a className="btn btn-primary" href="#about" onClick={scrollTo('about')}>
                  저를 소개합니다 <span aria-hidden="true">↗</span>
                </a>
                <a className="btn btn-secondary" href="#interests" onClick={scrollTo('interests')}>
                  관심사 둘러보기
                </a>
              </div>
              <div className="hero-note">
                <span className="dot" />
                <span>천천히 배우고, 꾸준히 만들고, 기록하기</span>
              </div>
            </div>
            <aside className="hero-card" aria-label="오늘의 공간 소개">
              <div className="card-top">
                <span>WELCOME TO MY SPACE</span>
                <span className="today-tag">Today</span>
              </div>
              <div className="decor-circle" aria-hidden="true" />
              <h2>
                호기심에서 시작해
                <br />
                경험으로 남깁니다.
              </h2>
              <p>완성된 결과뿐 아니라, 알아 가는 과정도 소중하게 기록합니다.</p>
              <div className="mini-list">
                <div className="mini-item">
                  <span className="mini-icon">
                    <svg {...svgProps} strokeWidth="1.7">
                      <rect x="3" y="4" width="18" height="16" rx="2" />
                      <path d="m7 15 3-3 3 2 4-5" />
                    </svg>
                  </span>
                  <span>
                    <strong>만들어 보기</strong>
                    <small>아이디어를 작은 결과물로</small>
                  </span>
                </div>
                <div className="mini-item">
                  <span className="mini-icon">
                    <svg {...svgProps} strokeWidth="1.7">
                      <path d="M12 3a7 7 0 0 0-4 12.7c.7.5 1 1.1 1 1.8h6c0-.7.3-1.3 1-1.8A7 7 0 0 0 12 3Z" />
                      <path d="M9 21h6M9 18h6" />
                    </svg>
                  </span>
                  <span>
                    <strong>새롭게 배우기</strong>
                    <small>궁금한 것을 직접 탐구하기</small>
                  </span>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section id="about">
          <div className="wrap about-grid">
            <div className="section-head">
              <div className="section-kicker">ABOUT ME</div>
              <h2 className="section-title">
                안녕하세요.
                <br />
                제 공간에 오신 것을 환영합니다.
              </h2>
              <p className="section-desc">관심 있는 것을 배우고 직접 시도하는 과정을 좋아합니다.</p>
            </div>
            <div className="about-text">
              <p>
                PlanGo.today는 제가 관심을 갖고 있는 것들과 직접 해 본 일들을 모아 두는{' '}
                <strong>개인 홈페이지</strong>입니다.
              </p>
              <p>
                새로운 기술을 익히고, 생활에 도움이 되는 도구를 만들어 보고, 더 나은 방법을 고민하는 일을
                즐깁니다. 때로는 작은 실험이 되고, 때로는 하나의 프로젝트가 되기도 합니다.
              </p>
              <p>
                이곳에는 멋진 결과만 보여주기보다 생각의 과정과 배운 점도 차근차근 쌓아 가려고 합니다.
                방문하시는 분께도 작은 아이디어나 새로운 영감이 되는 공간이면 좋겠습니다.
              </p>
            </div>
          </div>
        </section>

        <section id="interests">
          <div className="wrap">
            <div className="section-head">
              <div className="section-kicker">MY INTERESTS</div>
              <h2 className="section-title">요즘 관심을 두고 있는 것들</h2>
              <p className="section-desc">
                관심사는 계속 바뀌고 넓어집니다. 지금은 이런 주제들을 중심으로 배우고 기록합니다.
              </p>
            </div>
            <div className="interest-grid">
              <article className="interest-card">
                <div className="interest-icon">
                  <svg {...svgProps} strokeWidth="1.7">
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <path d="M8 8h8M8 12h5M8 16h8" />
                  </svg>
                </div>
                <h3>기술과 개발</h3>
                <p>웹과 소프트웨어를 배우고, 일상에서 쓸 수 있는 도구를 직접 만들어 봅니다.</p>
              </article>
              <article className="interest-card">
                <div className="interest-icon">
                  <svg {...svgProps} strokeWidth="1.7">
                    <path d="M3 3v18h18" />
                    <path d="m7 14 4-4 3 3 6-7" />
                  </svg>
                </div>
                <h3>아이디어와 프로젝트</h3>
                <p>떠오른 생각을 구체적인 계획으로 바꾸고, 하나씩 실행해 보는 과정에 관심이 있습니다.</p>
              </article>
              <article className="interest-card">
                <div className="interest-icon">
                  <svg {...svgProps} strokeWidth="1.7">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                </div>
                <h3>배움과 기록</h3>
                <p>새롭게 알게 된 것, 시행착오, 다시 참고하고 싶은 내용을 기록으로 남깁니다.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="project-section" id="projects">
          <div className="wrap">
            <div className="section-head">
              <div className="section-kicker">PROJECTS</div>
              <h2 className="section-title">직접 만들고 있는 것들</h2>
              <p className="section-desc">관심사를 실제 결과물로 이어 가는 프로젝트를 소개합니다.</p>
            </div>
            <div className="project-grid">
              <article className="project-card">
                <div className="project-top">
                  <span className="project-label">PERSONAL PROJECT</span>
                  <span className="project-status">진행 중</span>
                </div>
                <h3>PlanGo Agent</h3>
                <p>
                  키움증권 REST API를 이용하여 국내 미국주식 분할 무한매매를 사용자 설정에 맞춰 매매를 진행하는
                  서버기반 프로그램입니다. 서버구성을 구독으로 도와 드립니다.
                </p>
                <div className="project-tags-row">
                  <div className="project-tags">
                    <span>웹 개발</span>
                    <span>자동화 도구</span>
                    <span>지속적인 개선</span>
                  </div>
                  <a
                    className="btn project-cta agent-link"
                    href="https://agent.plango.today"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    PlanGo Agent <span aria-hidden="true">↗</span>
                  </a>
                </div>
              </article>
              <article className="project-card">
                <div className="project-top">
                  <span className="project-label">EV · CHARGING ROUTE</span>
                  <span className="project-status">앱 소개</span>
                </div>
                <h3>Charging Planner App</h3>
                <p>
                  전기차 이동을 더 편리하게 계획할 수 있도록 경로와 충전소 정보를 살펴보고, 주행 계획을 세우는
                  앱입니다.
                </p>
                <div className="project-tags-row">
                  <div className="project-tags">
                    <span>전기차 여행</span>
                    <span>경로 비교</span>
                    <span>충전소 정보</span>
                  </div>
                  <a
                    className="btn btn-primary project-cta"
                    href={CHARGING_PLANNER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Charging Planner 바로가기 <span aria-hidden="true">↗</span>
                  </a>
                </div>
              </article>
            </div>
            <p className="project-footnote">
              ※ 이 페이지의 프로젝트 설명은 소개용 문구입니다. 실제 진행 상황과 공개 가능한 내용에 맞춰 수정할 수
              있습니다.
            </p>
          </div>
        </section>

        <section id="contact">
          <div className="wrap">
            <div className="contact-box">
              <div>
                <h2>편하게 인사 나눠요.</h2>
                <p>궁금한 점이나 나누고 싶은 이야기가 있다면 연락해 주세요.</p>
              </div>
              <a className="btn btn-primary contact-link" href={MAILTO_URL} onClick={openContact}>
                문의하기 <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="wrap footer-inner">
          <span className="footer-brand">PlanGo.today</span>
          <span>개인적인 관심과 경험을 기록하는 공간</span>
          <span>© {new Date().getFullYear()} PlanGo.today</span>
        </div>
      </footer>

      {contactOpen && (
        <div className="contact-modal-backdrop" onClick={() => setContactOpen(false)}>
          <div
            className="contact-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="contact-modal-title">문의하기</h3>
            <div className="contact-modal-row">
              <span className="contact-modal-label">이메일</span>
              <strong>{CONTACT_EMAIL}</strong>
              <button
                type="button"
                className="contact-modal-copy"
                onClick={async () => setCopied(await copyText(CONTACT_EMAIL))}
              >
                {copied ? '복사됨' : '주소 복사'}
              </button>
            </div>
            <p className="contact-modal-hint">
              메일 프로그램이 열리지 않으면 주소를 복사해 사용하는 메일에서 보내주세요.
            </p>
            <div className="contact-modal-sub">다른 문의 방법</div>
            <div className="contact-modal-links">
              <a href={KAKAO_CHANNEL_URL} target="_blank" rel="noopener noreferrer">카카오톡 채널 1:1 상담 ↗</a>
              <a href={NAVER_CAFE_URL} target="_blank" rel="noopener noreferrer">PlanGo 네이버카페 Q&amp;A ↗</a>
            </div>
            <div className="contact-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setContactOpen(false)}>닫기</button>
              <a className="btn btn-primary" href={MAILTO_URL}>메일 보내기</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
